/* AFTERSOIL C / ink diffusion. Pointer-only progressive enhancement.
 * Original images, native home link, dimensions and alternate-logo states remain authoritative.
 * No entrance animation, timers, demo controls or navigation interception. */
(() => {
  if (customElements.get('aftersoil-logo-ui')) return;
  const VERTEX = `attribute vec2 aPosition; varying vec2 vUv;
    void main(){vUv=aPosition*.5+.5;gl_Position=vec4(aPosition,0.,1.);}`;
  const FRAGMENT = `precision highp float;
    varying vec2 vUv;
    uniform sampler2D uLogo;
    uniform vec2 uWakes[10];
    uniform float uAges[10];
    vec4 pigment(vec2 p){
      vec2 uv=(p-.5)/.58+.5;
      if(uv.x<0.||uv.y<0.||uv.x>1.||uv.y>1.)return vec4(0.);
      return texture2D(uLogo,uv);
    }
    void main(){
      vec2 p=vUv, displacement=vec2(0.);
      float wet=0.;
      for(int i=0;i<10;i++){
        float age=uAges[i];
        if(age>=0.&&age<2.8){
          vec2 d=p-uWakes[i];
          float life=exp(-age*2.5), field=exp(-dot(d,d)*38.);
          wet+=field*life*.35;
          vec2 n=vec2(sin(p.y*28.+p.x*14.+age*2.),cos(p.x*25.-p.y*13.-age*2.));
          displacement+=n*field*life*.007;
          displacement-=d*field*life*.027;
        }
      }
      displacement*=min(1.,.135/(length(displacement)+.0001));
      vec2 q=p+displacement;
      vec4 base=pigment(q), color=base;
      float alpha=base.a;
      if(wet>.0001){
        wet=min(wet,1.);
        float spread=wet*.024, body=alpha, rim=0.;
        for(int j=0;j<8;j++){
          float angle=float(j)*.785398;
          vec2 o=vec2(cos(angle),sin(angle));
          vec4 b=pigment(q+o*spread*.58), r=pigment(q+o*spread);
          body=max(body,b.a);rim+=r.a;
          if(b.a>color.a)color=b;
          if(r.a>color.a)color=r;
        }
        alpha=max(alpha,mix(alpha,body,wet*.87));
        alpha=max(alpha,rim*.125*wet*.25);
      }
      gl_FragColor=vec4(color.rgb,alpha);
    }`;

  class LogoUI extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.wakes = []; this.layers = []; this.raf = 0; this.ready = false; this.inView = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerMedia = matchMedia('(any-hover: hover) and (any-pointer: fine)');
      const signal = this.controller.signal;
      this.addEventListener('pointerenter', e => { this.lastPointer = null; this.pointer(e); }, {signal});
      this.addEventListener('pointermove', e => this.pointer(e), {signal});
      this.addEventListener('pointerleave', () => { this.lastPointer = null; }, {signal});
      const preference = () => { this.rest(); if (!this.motion.matches && this.pointerMedia.matches) this.setup(); };
      this.motion.addEventListener('change', preference, {signal});
      this.pointerMedia.addEventListener('change', preference, {signal});
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.rest(); }, {signal});
      this.io = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (!this.inView) this.rest();
        else if (!this.motion.matches && this.pointerMedia.matches) this.setup();
      }, {threshold: .1});
      this.io.observe(this);
      this.ro = new ResizeObserver(() => { this.rest(); this.resize(); });
      this.ro.observe(this);
    }
    async setup() {
      if (this.ready || this.loading || this.failed || !this.isConnected) return;
      this.loading = true;
      const signal = this.controller.signal;
      try {
        const images = [...this.querySelectorAll('img.header__logo-image')];
        if (!images.length) throw Error('No logo image');
        for (const image of images) {
          const source = new Image(); source.crossOrigin = 'anonymous';
          source.src = image.currentSrc || image.src;
          await source.decode();
          if (signal.aborted) return;
          const canvas = document.createElement('canvas'); canvas.setAttribute('aria-hidden', 'true');
          const gl = canvas.getContext('webgl', {alpha:true, antialias:false, premultipliedAlpha:false, depth:false, stencil:false, powerPreference:'low-power'});
          if (!gl) throw Error('WebGL unavailable');
          const layer = {canvas, gl, image}; this.layers.push(layer);
          canvas.addEventListener('webglcontextlost', e => { e.preventDefault(); this.failed = true; this.ready = false; this.rest(); }, {signal});
          const compile = (type, text) => {
            const shader=gl.createShader(type);gl.shaderSource(shader,text);gl.compileShader(shader);
            if (!gl.getShaderParameter(shader,gl.COMPILE_STATUS)) throw Error('Logo shader unavailable');
            return shader;
          };
          const vs=compile(gl.VERTEX_SHADER,VERTEX), fs=compile(gl.FRAGMENT_SHADER,FRAGMENT);
          const program=gl.createProgram();layer.program=program;gl.attachShader(program,vs);gl.attachShader(program,fs);gl.linkProgram(program);
          gl.deleteShader(vs);gl.deleteShader(fs);
          if (!gl.getProgramParameter(program,gl.LINK_STATUS)) throw Error('Logo shader unavailable');
          gl.useProgram(program);
          const buffer=gl.createBuffer();layer.buffer=buffer;gl.bindBuffer(gl.ARRAY_BUFFER,buffer);
          gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,-1,1,1,-1,1,1]),gl.STATIC_DRAW);
          const pos=gl.getAttribLocation(program,'aPosition');gl.enableVertexAttribArray(pos);gl.vertexAttribPointer(pos,2,gl.FLOAT,false,0,0);
          layer.uniforms={wakes:gl.getUniformLocation(program,'uWakes[0]'), ages:gl.getUniformLocation(program,'uAges[0]')};
          const texture=gl.createTexture();layer.texture=texture;gl.bindTexture(gl.TEXTURE_2D,texture);
          gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL,true);
          gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,source);
          gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
          // Insert before the images so Impact's native :last-child logo rule stays valid.
          this.insertBefore(canvas, this.firstChild);
        }
        this.ready = true; this.dataset.ready = 'true'; this.resize();
      } catch (_) {
        this.failed = true; this.dataset.fallback = 'static'; this.rest(); this.dispose();
      } finally { this.loading = false; }
    }
    resize() {
      const box = this.getBoundingClientRect(), dpr = Math.min(devicePixelRatio || 1, 2);
      for (const {canvas,gl} of this.layers) {
        canvas.width=Math.max(1,Math.round(box.width/.58*dpr));
        canvas.height=Math.max(1,Math.round(box.height/.58*dpr));
        gl.viewport(0,0,canvas.width,canvas.height);
      }
    }
    allowed() { return this.ready && this.inView && !document.hidden && !this.motion.matches && this.pointerMedia.matches; }
    pointer(e) {
      if (e.pointerType === 'touch' || !this.allowed()) return;
      const box=this.getBoundingClientRect();
      if (!box.width || !box.height) return;
      const x=((e.clientX-box.left)/box.width-.5)*.58+.5;
      const y=(.5-(e.clientY-box.top)/box.height)*.58+.5;
      const now=performance.now(), prev=this.lastPointer;
      if (prev && (now-prev.t<24 || Math.hypot(x-prev.x,y-prev.y)<.0005)) return;
      this.lastPointer={x,y,t:now};this.wakes.push({x,y,t:now});
      if (this.wakes.length>10) this.wakes.shift();
      if (!this.raf) { this.draw(now);this.setAttribute('data-rendering','');this.raf=requestAnimationFrame(t=>this.tick(t)); }
    }
    tick(now) {
      this.raf=0;
      this.wakes=this.wakes.filter(w=>now-w.t<2800);
      if (!this.allowed() || !this.wakes.length) { this.rest(); return; }
      this.draw(now);this.raf=requestAnimationFrame(t=>this.tick(t));
    }
    draw(now) {
      const wakes=new Float32Array(20),ages=new Float32Array(10).fill(-1);
      this.wakes.forEach((w,i)=>{wakes.set([w.x,w.y],i*2);ages[i]=(now-w.t)/1000;});
      for (const {canvas,gl,image,uniforms} of this.layers) {
        canvas.style.opacity=getComputedStyle(image).opacity;
        gl.uniform2fv(uniforms.wakes,wakes);gl.uniform1fv(uniforms.ages,ages);
        gl.clearColor(0,0,0,0);gl.clear(gl.COLOR_BUFFER_BIT);gl.drawArrays(gl.TRIANGLES,0,6);
      }
    }
    rest() { cancelAnimationFrame(this.raf);this.raf=0;this.wakes=[];this.lastPointer=null;this.removeAttribute('data-rendering'); }
    dispose() {
      for (const {gl,texture,buffer,program,canvas} of this.layers || []) {
        gl.deleteTexture(texture);gl.deleteBuffer(buffer);gl.deleteProgram(program);
        gl.getExtension('WEBGL_lose_context')?.loseContext();canvas.remove();
      }
      this.layers=[];this.ready=false;
    }
    disconnectedCallback() {
      this.rest();this.controller?.abort();this.controller=null;
      this.io?.disconnect();this.ro?.disconnect();this.dispose();this.loading=false;this.failed=false;
      delete this.dataset.ready;delete this.dataset.fallback;
    }
  }
  customElements.define('aftersoil-logo-ui', LogoUI);
})();
